import { useState, useEffect, useMemo } from 'react'
import { Printer, FileCode, AlertCircle, Loader2, UtensilsCrossed } from 'lucide-react'
import { useApp } from '../App'
import { menusAPI, airtableAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'
import { buildHavnContext, mdTable, downloadMD, buildFilename, fmtNum } from '../lib/mdExport'
import { componentKitchenName } from '../lib/plating'

// dish.category → key used in airtable_import meal_counts payload.
// Most categories map 1:1; only legacy aliases (chef → cheat, wellness → wellness_shots) translate.
const CATEGORY_TO_COUNT_KEY = {
  chicken: 'chicken', chicken_2: 'chicken_2',
  beef: 'beef',
  seafood: 'seafood', seafood_2: 'seafood_2',
  veg: 'veg', pasta: 'pasta',
  salad: 'salad', salad_2: 'salad_2',
  chef: 'cheat', cheat: 'cheat',
  broth: 'broth', broth_2: 'broth_2',
  oats: 'oats',
  chia: 'chia', chia_2: 'chia_2',
  wellness: 'wellness_shots', wellness_shots: 'wellness_shots',
  date_balls: 'date_balls',
}

// ══════════════════════════════════════════════════════════════════════════════
// CATEGORY CONFIG — kitchen-facing labels
// ══════════════════════════════════════════════════════════════════════════════

const CATEGORY_CONFIG = {
  chicken:        { label: 'Chicken',        color: 'bg-green-100 text-green-700 border-green-200',   dot: 'bg-green-600' },
  chicken_2:      { label: 'Chicken 2',      color: 'bg-green-100 text-green-700 border-green-200',   dot: 'bg-green-600' },
  beef:           { label: 'Beef',           color: 'bg-red-100 text-red-700 border-red-200',         dot: 'bg-red-600' },
  seafood:        { label: 'Seafood',        color: 'bg-cyan-100 text-cyan-700 border-cyan-200',      dot: 'bg-cyan-600' },
  seafood_2:      { label: 'Seafood 2',      color: 'bg-teal-100 text-teal-700 border-teal-200',      dot: 'bg-teal-700' },
  veg:            { label: 'Vegetarian',     color: 'bg-lime-100 text-lime-700 border-lime-200',      dot: 'bg-lime-600' },
  chef:           { label: 'Chef Special',   color: 'bg-purple-100 text-purple-700 border-purple-200',dot: 'bg-purple-600' },
  cheat:          { label: 'Cheat Meal',     color: 'bg-amber-100 text-amber-700 border-amber-200',   dot: 'bg-amber-600' },
  pasta:          { label: 'Pasta',          color: 'bg-stone-100 text-stone-700 border-stone-200',   dot: 'bg-stone-500' },
  salad:          { label: 'Salad',          color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-600' },
  salad_2:        { label: 'Salad 2',        color: 'bg-emerald-50 text-emerald-600 border-emerald-200',  dot: 'bg-emerald-500' },
  broth:          { label: 'Broth',          color: 'bg-orange-100 text-orange-700 border-orange-200',dot: 'bg-orange-600' },
  broth_2:        { label: 'Broth 2',        color: 'bg-orange-50 text-orange-600 border-orange-200', dot: 'bg-orange-500' },
  oats:           { label: 'Overnight Oats', color: 'bg-yellow-100 text-yellow-800 border-yellow-200',dot: 'bg-yellow-600' },
  chia:           { label: 'Chia Pudding',   color: 'bg-pink-100 text-pink-700 border-pink-200',      dot: 'bg-pink-600' },
  chia_2:         { label: 'Chia 2',         color: 'bg-pink-50 text-pink-600 border-pink-200',       dot: 'bg-pink-400' },
}

const COMPONENT_TYPE_CONFIG = {
  protein:   { label: 'Protein',     color: 'bg-red-50 text-red-700 border-red-200',         order: 1 },
  vegetable: { label: 'Vegetable',   color: 'bg-green-50 text-green-700 border-green-200',   order: 2 },
  carb:      { label: 'Carb',        color: 'bg-amber-50 text-amber-800 border-amber-200',   order: 3 },
  sauce:     { label: 'Sauce',       color: 'bg-purple-50 text-purple-700 border-purple-200', order: 4 },
  base:      { label: 'Base',        color: 'bg-blue-50 text-blue-700 border-blue-200',      order: 5 },
  seasoning: { label: 'Seasoning',   color: 'bg-stone-50 text-stone-700 border-stone-200',   order: 6 },
  garnish:   { label: 'Garnish',     color: 'bg-stone-50 text-stone-700 border-stone-200',   order: 6 },
}

const MEAL_CATEGORIES = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'chef', 'cheat', 'pasta', 'salad', 'salad_2']
const SIDE_CATEGORIES = ['broth', 'broth_2', 'oats', 'chia', 'chia_2']

// ══════════════════════════════════════════════════════════════════════════════
// PORTION FORMATTING
// ══════════════════════════════════════════════════════════════════════════════

function formatPortion(oz) {
  if (oz == null || Number.isNaN(Number(oz))) return '—'
  const n = Number(oz)
  // Trim trailing zeros: 6.50 → 6.5, 3.00 → 3
  const rounded = Math.round(n * 100) / 100
  return `${rounded} oz`
}

// ══════════════════════════════════════════════════════════════════════════════
// MEAL COUNT STRIP — how many to plate (glass / plastic, excluding customs)
// Kitchen plates standard meals only. Customs are handled separately downstream.
// ══════════════════════════════════════════════════════════════════════════════

function MealCountStrip({ counts, category }) {
  if (!counts) {
    return (
      <div className="px-5 py-2.5 bg-havn-cream-50 border-b border-havn-cream-200 text-xs text-havn-gray-400 italic">
        Meal counts not yet imported for this menu.
      </div>
    )
  }

  // Sides — total only, no container split (broth/oats/chia variants)
  const isSide = SIDE_CATEGORIES.includes(category)
  if (isSide) {
    return (
      <div className="px-5 py-3 bg-havn-green-50 border-b border-havn-cream-200 flex items-baseline gap-3">
        <span className="text-[10px] uppercase tracking-widest text-havn-green-800 font-semibold">
          Plate
        </span>
        <span className="font-display text-2xl font-bold text-havn-green-900 tabular-nums leading-none">
          {counts.total ?? 0}
        </span>
        <span className="text-xs text-havn-gray-500">total</span>
      </div>
    )
  }

  // Meal slots — glass + plastic standard (customs excluded), with custom note
  const glassStd = counts.glass_standard ?? 0
  const plasticStd = counts.plastic_standard ?? 0
  const totalCustom = counts.total_custom ?? 0

  return (
    <div className="px-5 py-3 bg-havn-green-50 border-b border-havn-cream-200">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest text-havn-green-800 font-semibold">
          Plate (standard, customs excluded)
        </span>
      </div>
      <div className="flex items-stretch gap-3">
        <div className="flex-1 bg-white rounded-lg px-3 py-2 text-center border border-havn-cream-200">
          <div className="text-[10px] uppercase tracking-wider text-havn-gray-500 font-medium">Glass</div>
          <div className="font-display text-3xl font-bold text-havn-green-900 tabular-nums leading-none mt-1">
            {glassStd}
          </div>
        </div>
        <div className="flex-1 bg-white rounded-lg px-3 py-2 text-center border border-havn-cream-200">
          <div className="text-[10px] uppercase tracking-wider text-havn-gray-500 font-medium">Plastic</div>
          <div className="font-display text-3xl font-bold text-havn-green-900 tabular-nums leading-none mt-1">
            {plasticStd}
          </div>
        </div>
        <div className="flex-1 bg-havn-cream-100 rounded-lg px-3 py-2 text-center border border-havn-cream-200">
          <div className="text-[10px] uppercase tracking-wider text-havn-gray-500 font-medium">Total</div>
          <div className="font-display text-3xl font-bold text-havn-charcoal tabular-nums leading-none mt-1">
            {glassStd + plasticStd}
          </div>
        </div>
      </div>
      {totalCustom > 0 && (
        <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          <span><strong>{totalCustom}</strong> custom {totalCustom === 1 ? 'order' : 'orders'} on this dish — plated separately, not included above</span>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// DISH PLATING CARD — one dish with its component portions
// ══════════════════════════════════════════════════════════════════════════════

function DishPlatingCard({ dish, counts }) {
  const config = CATEGORY_CONFIG[dish.category] || CATEGORY_CONFIG.chicken
  const components = (dish.components || []).slice().sort((a, b) => {
    const oa = COMPONENT_TYPE_CONFIG[a.component_type]?.order ?? 99
    const ob = COMPONENT_TYPE_CONFIG[b.component_type]?.order ?? 99
    if (oa !== ob) return oa - ob
    return (a.name || '').localeCompare(b.name || '')
  })

  return (
    <div className="plating-card bg-white rounded-xl border border-havn-cream-300 overflow-hidden break-inside-avoid">
      {/* Header — dish name + category */}
      <div className="px-5 py-4 border-b border-havn-cream-200 bg-havn-cream-50 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${config.dot}`} />
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-havn-green-900 truncate">
              {dish.name}
            </h3>
            {dish.cuisine && (
              <p className="text-xs text-havn-gray-500 mt-0.5">{dish.cuisine}</p>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border flex-shrink-0 ${config.color}`}>
          {config.label}
        </span>
      </div>

      {/* Meal count strip — how many to plate this week */}
      <MealCountStrip counts={counts} category={dish.category} />

      {/* Components table — the plating guide */}
      {components.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-havn-gray-400">
          No components linked to this dish.
        </div>
      ) : (
        <table className="w-full plating-table">
          <thead className="bg-white">
            <tr className="text-left text-[11px] uppercase tracking-widest text-havn-gray-400 border-b border-havn-cream-200">
              <th className="px-5 py-2.5 font-medium">Type</th>
              <th className="px-5 py-2.5 font-medium">Component</th>
              <th className="px-5 py-2.5 font-medium text-right">Portion</th>
            </tr>
          </thead>
          <tbody>
            {components.map((comp, i) => {
              const rawType = comp.component_type || 'other'
              const typeConfig = COMPONENT_TYPE_CONFIG[rawType] || {
                label: rawType.charAt(0).toUpperCase() + rawType.slice(1),
                color: 'bg-gray-50 text-gray-700 border-gray-200',
              }
              return (
                <tr
                  key={comp.id ?? i}
                  className="border-b border-havn-cream-100 last:border-b-0"
                >
                  <td className="px-5 py-3 align-middle">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-havn-charcoal">
                        {componentKitchenName(comp)}
                      </span>
                      {comp.is_swapped && (
                        <span
                          className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200"
                          title={`Original: ${comp.original_name}`}
                        >
                          swapped
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 align-middle text-right">
                    <span className="inline-block font-display text-base font-semibold text-havn-green-800 tabular-nums">
                      {formatPortion(comp.portion_oz)}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function Plating() {
  const { currentMenu, currentCity, cities } = useApp()
  const [menu, setMenu] = useState(null)
  const [mealCounts, setMealCounts] = useState(null)
  const [countsImportedAt, setCountsImportedAt] = useState(null)
  const [loading, setLoading] = useState(true)
  const { error } = useToastActions()

  useEffect(() => {
    if (!currentMenu) {
      setMenu(null)
      setMealCounts(null)
      setCountsImportedAt(null)
      setLoading(false)
      return
    }
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const [menuData, importData] = await Promise.all([
          menusAPI.get(currentMenu),
          airtableAPI.getLatest(currentCity, currentMenu).catch(() => null),
        ])
        if (cancelled) return
        setMenu(menuData)
        setMealCounts(importData?.meal_counts || null)
        setCountsImportedAt(importData?.imported_at || null)
      } catch (err) {
        if (!cancelled) error('Failed to load menu: ' + err.message)
        console.error(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [currentMenu, currentCity])

  const { meals, sides } = useMemo(() => {
    const dishes = menu?.dishes || []
    const mealOrder = MEAL_CATEGORIES
    const sideOrder = SIDE_CATEGORIES
    const meals = dishes
      .filter(d => mealOrder.includes(d.category))
      .sort((a, b) => mealOrder.indexOf(a.category) - mealOrder.indexOf(b.category))
    const sides = dishes
      .filter(d => sideOrder.includes(d.category))
      .sort((a, b) => sideOrder.indexOf(a.category) - sideOrder.indexOf(b.category))
    return { meals, sides }
  }, [menu])

  const cityName = cities.find(c => c.id === currentCity)?.name || currentCity?.toUpperCase()

  function handlePrint() {
    window.print()
  }

  function handleExportMD() {
    if (!menu) return
    const cityId = currentCity
    let md = buildHavnContext({
      docType: 'Plating Guide',
      docPurpose: 'Per-dish portion guide for the kitchen: components and ounce weights, plus how many to plate (Glass / Plastic, standard only — customs handled separately).',
      city: cityId,
      menu: menu.name,
      weekOf: menu.week_of,
    })

    md += `# Plating Guide — ${menu.name}\n\n`
    if (countsImportedAt) {
      md += `_Meal counts imported ${new Date(countsImportedAt).toISOString()}._\n\n`
    } else {
      md += `> ⚠️ No meal counts imported yet for this menu. Plate counts below will be missing.\n\n`
    }

    const renderDish = (dish) => {
      const config = CATEGORY_CONFIG[dish.category] || {}
      const counts = mealCounts?.[CATEGORY_TO_COUNT_KEY[dish.category]]
      let out = `### ${dish.name}\n\n`
      out += `- **Slot:** \`${dish.category}\` (${config.label || dish.category})\n`
      if (dish.cuisine) out += `- **Cuisine:** ${dish.cuisine}\n`

      if (counts) {
        const isSide = dish.category === 'broth' || dish.category === 'chia'
        if (isSide) {
          out += `- **Plate (total):** ${counts.total ?? 0}\n`
        } else {
          const glassStd = counts.glass_standard ?? 0
          const plasticStd = counts.plastic_standard ?? 0
          out += `- **Plate (standard):** Glass ${glassStd} · Plastic ${plasticStd} · Total ${glassStd + plasticStd}\n`
          if ((counts.total_custom ?? 0) > 0) {
            out += `- **Customs (plated separately, NOT included above):** ${counts.total_custom}\n`
          }
        }
      } else {
        out += `- **Plate:** _no counts imported_\n`
      }
      out += `\n`

      const components = (dish.components || []).slice().sort((a, b) => {
        const oa = COMPONENT_TYPE_CONFIG[a.component_type]?.order ?? 99
        const ob = COMPONENT_TYPE_CONFIG[b.component_type]?.order ?? 99
        if (oa !== ob) return oa - ob
        return (a.name || '').localeCompare(b.name || '')
      })

      if (components.length === 0) {
        out += `_No components linked to this dish._\n\n`
        return out
      }

      out += mdTable(
        ['Type', 'Component', 'Portion (oz)', 'Notes'],
        components.map((c) => [
          COMPONENT_TYPE_CONFIG[c.component_type]?.label || c.component_type || '',
          c.name || '',
          fmtNum(c.portion_oz),
          c.is_swapped ? `swapped from "${c.original_name || ''}"` : '',
        ]),
      )
      out += `\n\n`
      return out
    }

    if (meals.length) {
      md += `## Meals (${meals.length})\n\n`
      meals.forEach((d) => { md += renderDish(d) })
    }
    if (sides.length) {
      md += `## Sides (${sides.length})\n\n`
      sides.forEach((d) => { md += renderDish(d) })
    }

    downloadMD(buildFilename('plating-guide', { city: cityId, menu: menu.name }), md)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-havn-green-700 animate-spin" />
      </div>
    )
  }

  if (!currentMenu) {
    return (
      <div className="bg-white rounded-xl border border-havn-cream-300 p-10 text-center">
        <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-1">
          No menu selected
        </h3>
        <p className="text-sm text-havn-gray-500">
          Pick a menu from the sidebar to view its plating guide.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Print-only styles — scoped to this page */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { background: white !important; }
          /* Hide app chrome when printing */
          aside, header, .floating-tools, button.print-hide { display: none !important; }
          main { padding: 0 !important; overflow: visible !important; }
          .plating-print-root { margin: 0 !important; padding: 0 !important; }
          .plating-card {
            break-inside: avoid;
            page-break-inside: avoid;
            box-shadow: none !important;
            border: 1px solid #E5E0D6 !important;
            margin-bottom: 14px !important;
          }
          .plating-table { font-size: 11pt; }
          .plating-header-print { display: flex !important; }
          .plating-header-screen { display: none !important; }
        }
        @media screen {
          .plating-header-print { display: none; }
        }
      `}</style>

      {/* Screen header — toolbar with Print button */}
      <div className="plating-header-screen flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-havn-gray-500">
            Portion guide for the kitchen — components, ounces, and meal counts per dish. Print as PDF for wall posting.
          </p>
          {countsImportedAt && (
            <p className="text-xs text-havn-gray-400 mt-1">
              Meal counts as of {new Date(countsImportedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} — refresh via the <strong>Meal Counts</strong> page if stale.
            </p>
          )}
          {!countsImportedAt && menu && (
            <p className="text-xs text-amber-700 mt-1">
              ⚠️ No meal counts imported yet for this menu. Run an Airtable import on the <strong>Meal Counts</strong> page first.
            </p>
          )}
        </div>
        <div className="print-hide flex items-center gap-2">
          <button
            onClick={handleExportMD}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-havn-green-700 text-havn-green-700 hover:bg-havn-green-700 hover:text-white rounded-lg font-medium text-sm transition-colors shadow-soft"
            title="Download a markdown file optimized for feeding into an LLM (e.g. Claude Code)"
          >
            <FileCode className="w-4 h-4" />
            Export for AI
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2 bg-havn-green-700 hover:bg-havn-green-800 text-white rounded-lg font-medium text-sm transition-colors shadow-soft"
          >
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Print-only masthead — shown only on printed page */}
      <div className="plating-header-print items-end justify-between pb-3 mb-2 border-b-2 border-havn-green-900">
        <div>
          <h1 className="font-display text-3xl font-semibold text-havn-green-900">
            Plating Guide
          </h1>
          <p className="text-sm text-havn-gray-600 mt-1">
            {menu?.name} · {cityName}
          </p>
          {countsImportedAt && (
            <p className="text-xs text-havn-gray-500 mt-0.5">
              Meal counts as of {new Date(countsImportedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
        </div>
        <div className="text-right text-xs text-havn-gray-500">
          <div className="font-medium text-havn-charcoal">Havn Club · Culinary Ops</div>
          <div>Printed {new Date().toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</div>
        </div>
      </div>

      <div className="plating-print-root space-y-6">
        {/* Meals */}
        {meals.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <UtensilsCrossed className="w-4 h-4 text-havn-green-700" />
              <h2 className="font-display text-xl font-semibold text-havn-green-900">
                Meals
              </h2>
              <span className="text-xs text-havn-gray-400">· {meals.length}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {meals.map(dish => (
                <DishPlatingCard key={dish.dish_id} dish={dish} counts={mealCounts?.[CATEGORY_TO_COUNT_KEY[dish.category]]} />
              ))}
            </div>
          </section>
        )}

        {/* Sides */}
        {sides.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-display text-xl font-semibold text-havn-green-900">
                Sides
              </h2>
              <span className="text-xs text-havn-gray-400">· {sides.length}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sides.map(dish => (
                <DishPlatingCard key={dish.dish_id} dish={dish} counts={mealCounts?.[CATEGORY_TO_COUNT_KEY[dish.category]]} />
              ))}
            </div>
          </section>
        )}

        {meals.length === 0 && sides.length === 0 && (
          <div className="bg-white rounded-xl border border-havn-cream-300 p-10 text-center">
            <AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" />
            <h3 className="font-display text-lg font-semibold text-havn-charcoal mb-1">
              This menu has no dishes yet
            </h3>
            <p className="text-sm text-havn-gray-500">
              Add dishes in the Menu Builder to see plating portions here.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
